import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ErrorBoundary from './components/Shared/UI/ErrorBoundary';
import DashboardSkeleton from './components/Admin/DashboardSkeleton';
import AppUpdater from './components/Shared/UI/AppUpdater';

// Lazy load components for code splitting
const AuthRedirect = lazy(() => import('./components/Auth/AuthRedirect'));
const ProtectedRoute = lazy(() => import('./components/Auth/ProtectedRoute'));
const Home = lazy(() => import('./components/Public/Pages/Home/Home'));
const DriverLogin = lazy(() => import('./components/Auth/DriverLogin'));
const DriverPermissionGate = lazy(() => import('./components/Driver/Layout/DriverPermissionGate'));
const DriverDashboard = lazy(() => import('./components/Driver/Layout/DriverDashboard'));
const AdminLogin = lazy(() => import('./components/Auth/AdminLogin'));
const AdminDashboard = lazy(() => import('./components/Admin/AdminDashboard'));
const NotFound = lazy(() => import('./components/Public/Pages/Misc/NotFound'));
const CustomerTrack = lazy(() => import('./components/Customer/CustomerTrack'));
const CustomerLogin = lazy(() => import('./components/Auth/CustomerAuth'));
const PrivacyPolicy = lazy(() => import('./components/Public/Pages/Legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/Public/Pages/Legal/TermsOfService'));
const ServiceRequest = lazy(() => import('./components/Customer/ServiceRequest'));
const PartnerPage = lazy(() => import('./components/Public/Pages/Partner/PartnerPage'));
const HeavyMachineryPage = lazy(() => import('./components/Public/Pages/HeavyMachinery/HeavyMachineryPage'));

// Loading fallback component
const LoadingFallback = () => {
  const isAdminVerified = localStorage.getItem('isAdminVerified') === 'true';
  const isDarkMode = localStorage.getItem('adminTheme') !== 'light';

  if (isAdminVerified) {
    return <DashboardSkeleton isDarkMode={isDarkMode} />;
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      background: '#FFFFFF',
      color: '#FAB400',
      fontFamily: "'Inter', sans-serif"
    }}>
      <div style={{ fontSize: '1.5rem', fontWeight: 800, letterSpacing: '4px', marginBottom: '10px' }}>RSA</div>
      <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#00000060', letterSpacing: '2px' }}>ROADSIDE COLLABORATIVE</div>
    </div>
  );
};

function App() {
  return (
    <Router>
      <ErrorBoundary>
        <AppUpdater>
          <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<AuthRedirect><Home /></AuthRedirect>} />
            <Route path="/driver-login" element={<AuthRedirect><DriverLogin /></AuthRedirect>} />
            <Route path="/track" element={<CustomerTrack />} />
            <Route path="/customer-login" element={<CustomerLogin />} />
            <Route path="/services" element={<ServiceRequest />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/partner" element={<PartnerPage />} />
            <Route path="/heavy-machinery" element={<HeavyMachineryPage />} />
            <Route path="/driver-portal" element={
              <ProtectedRoute role="driver">
                <DriverPermissionGate>
                  <DriverDashboard />
                </DriverPermissionGate>
              </ProtectedRoute>
            } />
            <Route path="/admin" element={<AuthRedirect><AdminLogin /></AuthRedirect>} />
            <Route path="/admin-portal/*" element={
              <ProtectedRoute role="admin">
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </AppUpdater>
      </ErrorBoundary>
    </Router>
  );
}

export default App;
